import { isSameISOWeek } from 'date-fns';
import { DailyCalendarDay } from '../store/modules/boxes/types';

interface ProfitAndLossObj {
  profit: number;
  loss: number;
  total: number;
}

interface WinAndLossObj {
  win: number;
  loss: number;
  draw: number;
  percent: number;
}

function calcProfitAndLoss(days: DailyCalendarDay[]): ProfitAndLossObj {
  let profit = 0;
  let loss = 0;
  let total = 0;

  days.forEach(day => {
    profit += day.profit;
    loss += day.loss;
    total += day.total;
  });

  return {
    profit: parseFloat(profit.toFixed(2)),
    loss: parseFloat(loss.toFixed(2)),
    total: parseFloat(total.toFixed(2)),
  };
}

function calcWinAndLoss(days: DailyCalendarDay[]): WinAndLossObj {
  let win = 0;
  let loss = 0;
  let draw = 0;

  days.forEach(day => {
    win += day.winCount || 0;
    loss += day.lossCount || 0;
    draw += day.drawCount || 0;
  });

  const percentNotParsed = (100 * win) / (loss + win);
  const percent = parseFloat(percentNotParsed.toFixed(1));

  return { win, loss, draw, percent };
}

export function computeProfitAndLoss(dailyCalendar: DailyCalendarDay[], offsetHours: number) {
  const now = new Date();
  now.setTime(now.getTime() - offsetHours * 60 * 60 * 1000);

  const allDays = dailyCalendar;

  const daysOfYear = allDays.filter(day => {
    const d = new Date(day.date + 'T12:00:00Z');
    return d.getUTCFullYear() === now.getUTCFullYear();
  });

  const daysOfMonth = daysOfYear.filter(day => {
    const d = new Date(day.date + 'T12:00:00Z');
    return d.getUTCMonth() === now.getUTCMonth();
  });

  const daysOfWeek = daysOfYear.filter(day => {
    const d = new Date(day.date + 'T12:00:00Z');
    return isSameISOWeek(now, d);
  });

  const daysOfToday = daysOfWeek.filter(day => {
    const d = new Date(day.date + 'T12:00:00Z');
    return d.getUTCDate() === now.getUTCDate();
  });

  return {
    today: calcProfitAndLoss(daysOfToday),
    week: calcProfitAndLoss(daysOfWeek),
    month: calcProfitAndLoss(daysOfMonth),
    year: calcProfitAndLoss(daysOfYear),
    allTime: calcProfitAndLoss(allDays),
  };
}

export function computeWinAndLoss(dailyCalendar: DailyCalendarDay[], offsetHours: number) {
  const now = new Date();
  now.setTime(now.getTime() - offsetHours * 60 * 60 * 1000);

  const allDays = dailyCalendar;

  const daysOfYear = allDays.filter(day => {
    const d = new Date(day.date + 'T12:00:00Z');
    return d.getUTCFullYear() === now.getUTCFullYear();
  });

  const daysOfMonth = daysOfYear.filter(day => {
    const d = new Date(day.date + 'T12:00:00Z');
    return d.getUTCMonth() === now.getUTCMonth();
  });

  const daysOfWeek = daysOfYear.filter(day => {
    const d = new Date(day.date + 'T12:00:00Z');
    return isSameISOWeek(now, d);
  });

  const daysOfToday = daysOfWeek.filter(day => {
    const d = new Date(day.date + 'T12:00:00Z');
    return d.getUTCDate() === now.getUTCDate();
  });

  return {
    today: calcWinAndLoss(daysOfToday),
    week: calcWinAndLoss(daysOfWeek),
    month: calcWinAndLoss(daysOfMonth),
    year: calcWinAndLoss(daysOfYear),
    allTime: calcWinAndLoss(allDays),
  };
}

export function computeGoalsProgress(dailyCalendar: DailyCalendarDay[], offsetHours: number) {
  const now = new Date();
  now.setTime(now.getTime() - offsetHours * 60 * 60 * 1000);

  let progressToday = 0;
  let progressWeek = 0;
  let progressMonth = 0;

  dailyCalendar.forEach(day => {
    const d = new Date(day.date + 'T12:00:00Z');

    if (d.getUTCFullYear() !== now.getUTCFullYear()) return;
    if (d.getUTCMonth() !== now.getUTCMonth()) return;

    progressMonth += day.total;

    if (isSameISOWeek(now, d)) {
      progressWeek += day.total;

      if (d.getUTCDate() === now.getUTCDate()) {
        progressToday += day.total;
      }
    }
  });

  return {
    progressToday: parseFloat(progressToday.toFixed(2)),
    progressWeek: parseFloat(progressWeek.toFixed(2)),
    progressMonth: parseFloat(progressMonth.toFixed(2)),
  };
}
